import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  Text,
  RefreshControl,
  Pressable,
  Modal,
  Platform,
  Alert,
} from 'react-native';
import { Image } from 'expo-image';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ionicons, Feather } from '@expo/vector-icons';
import { useSetAtom } from 'jotai';
import * as ImagePicker from 'expo-image-picker';
import { doc, updateDoc } from 'firebase/firestore';
import PostListItem from '../../components/PostListItem';
import { fetchPostsByUserId, fetchUserKarma, fetchUserPostCount } from '../../services/postService';
import { Post } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { authSheetVisibleAtom, usernameSetupModalVisibleAtom } from '../../atoms';
import PixelContainer from '../../components/PixelContainer';
import UsernameSetupModal from '../../components/UsernameSetupModal';
import { fontStyle } from '../../config/fonts';
import { canChangeUsername, getTimeUntilUsernameChange, formatTimeRemaining } from '../../services/usernameService';
import { uploadImage } from '../../utils/firebaseImages';
import { db } from '../../lib/firebase';
import { preloadImages } from '../../utils/imageCache';

export default function ProfileScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { user, userProfile, needsUsername, updateUserProfile } = useAuth();
  const { isAuthenticated } = useRequireAuth();
  const setAuthSheetVisible = useSetAtom(authSheetVisibleAtom);
  const setUsernameModalVisible = useSetAtom(usernameSetupModalVisibleAtom);
  const queryClient = useQueryClient();
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showCooldownModal, setShowCooldownModal] = useState(false);

  // Show username setup modal if needed (only when needsUsername changes to true)
  useEffect(() => {
    if (isAuthenticated && needsUsername) {
      // Small delay to let the profile render first
      const timer = setTimeout(() => {
        setUsernameModalVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, needsUsername]);

  // Preload and cache avatar image to prevent flickering
  useEffect(() => {
    if (userProfile?.image) {
      preloadImages([userProfile.image]);
    }
  }, [userProfile?.image]);

  // Fetch user's posts - use staleTime to show cached data instantly
  const {
    data,
    isLoading: postsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ['userPosts', user?.uid],
    queryFn: ({ pageParam }) => fetchPostsByUserId(user!.uid, pageParam),
    initialPageParam: null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.lastDoc : undefined,
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // Data stays fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  // Fetch user's karma - cache aggressively since it doesn't change often
  const { data: karma = 0, isLoading: karmaLoading } = useQuery({
    queryKey: ['userKarma', user?.uid],
    queryFn: () => fetchUserKarma(user!.uid),
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  // Fetch user's post count - cache aggressively
  const { data: postCount = 0, isLoading: postCountLoading } = useQuery({
    queryKey: ['userPostCount', user?.uid],
    queryFn: () => fetchUserPostCount(user!.uid),
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  const posts = data?.pages.flatMap((page) => page.posts) || [];

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleSignInPress = () => {
    setAuthSheetVisible(true);
  };

  const handleEditUsername = () => {
    if (canChange) {
      setUsernameModalVisible(true);
    } else if (timeRemaining > 0) {
      // Show cooldown message as modal
      setShowCooldownModal(true);
    }
  };

  const handleUsernameSet = (username: string) => {
    updateUserProfile({ username });
  };

  const handlePickImage = async () => {
    if (!user) return;

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setIsUploadingImage(true);
      try {
        const imageUrl = await uploadImage(result.assets[0].uri);

        // Update Firestore
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, { image: imageUrl });

        // Update local state
        updateUserProfile({ image: imageUrl });

        // Invalidate queries that might show user data
        queryClient.invalidateQueries({ queryKey: ['userPosts'] });
      } catch (error) {
        console.error('Error uploading profile image:', error);
        Alert.alert(t('common.error'), t('errors.saveFailed'));
      } finally {
        setIsUploadingImage(false);
      }
    }
  };

  // Memoize avatar image source and key to prevent flickering on refetch
  const avatarSource = useMemo(() => {
    if (userProfile?.image) {
      return { uri: userProfile.image };
    }
    return require('../../../assets/images/default-avatar.webp');
  }, [userProfile?.image]);

  // Stable key that only changes when the image URL actually changes
  const avatarKey = useMemo(() => {
    return userProfile?.image || 'default-avatar';
  }, [userProfile?.image]);

  const renderItem = useCallback(
    ({ item }: { item: Post }) => <PostListItem post={item} />,
    []
  );

  const renderFooter = () => {
    if (!isFetchingNextPage) return null;
    return (
      <View style={styles.footer}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  };

  const renderEmpty = () => {
    // Show loading indicator while fetching posts
    if (postsLoading) {
      return (
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      );
    }
    return (
      <View style={styles.emptyContainer}>
        <Ionicons name="document-text-outline" size={48} color={colors.textSecondary} />
        <Text style={[styles.emptyTitle, fontStyle, { color: colors.text }]}>
          {t('profile.noPosts')}
        </Text>
        <Text style={[styles.emptySubtitle, fontStyle, { color: colors.textSecondary }]}>
          {t('profile.createFirstPost')}
        </Text>
      </View>
    );
  };

  // Format join date - memoized to prevent recalculation on every render
  const joinDate = useMemo(() => {
    if (!userProfile?.createdAt) return '';
    const date = userProfile.createdAt.toDate?.() || new Date(userProfile.createdAt.seconds * 1000);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  }, [userProfile?.createdAt]);

  // Check if username can be changed
  const canChange = canChangeUsername(userProfile?.usernameChangedAt);
  const timeRemaining = getTimeUntilUsernameChange(userProfile?.usernameChangedAt);

  const renderHeader = useCallback(() => (
    <View style={[styles.header, { backgroundColor: colors.surface }]}>
      {/* Profile Image with Upload */}
      <Pressable onPress={handlePickImage} style={styles.avatarContainer} disabled={isUploadingImage}>
        <View style={styles.avatarWrapper}>
          <Image
            key={avatarKey}
            source={avatarSource}
            style={styles.avatar}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={0}
            priority="high"
            recyclingKey={avatarKey}
          />
          <View style={[styles.cameraOverlay, { backgroundColor: 'rgba(0,0,0,0.5)' }]}>
            {isUploadingImage ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Feather name="camera" size={20} color="#fff" />
            )}
          </View>
        </View>
      </Pressable>

      {/* Username with Edit Icon */}
      <View style={styles.usernameRow}>
        <Text style={[styles.username, fontStyle, { color: colors.text }]}>
          @{userProfile?.username || 'unknown'}
        </Text>
        <Pressable
          onPress={handleEditUsername}
          style={[styles.editIconButton, !canChange && styles.editIconDisabled]}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons
            name="pencil"
            size={16}
            color={canChange ? colors.primary : colors.textSecondary}
          />
        </Pressable>
      </View>

      {/* Stats Row */}
      <View style={styles.statsRow}>
        <View style={styles.statItem}>
          <Text style={[styles.statValue, fontStyle, { color: colors.text }]}>
            {karmaLoading ? '-' : karma}
          </Text>
          <Text style={[styles.statLabel, fontStyle, { color: colors.textSecondary }]}>
            {t('profile.karma')}
          </Text>
        </View>
        <View style={[styles.statDivider, { backgroundColor: colors.border }]} />
        <View style={styles.statItem}>
          <Text style={[styles.statValue, fontStyle, { color: colors.text }]}>
            {postCountLoading ? '-' : postCount}
          </Text>
          <Text style={[styles.statLabel, fontStyle, { color: colors.textSecondary }]}>
            {t('profile.posts')}
          </Text>
        </View>
      </View>

      {/* Member Since */}
      <Text style={[styles.memberSince, fontStyle, { color: colors.textSecondary }]}>
        {t('profile.memberSince')} {joinDate}
      </Text>

      {/* Posts Section Title */}
      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionTitle, fontStyle, { color: colors.text }]}>
          {t('profile.yourPosts')}
        </Text>
      </View>
    </View>
  ), [
    colors,
    userProfile?.username,
    avatarSource,
    avatarKey,
    isUploadingImage,
    karmaLoading,
    karma,
    postCountLoading,
    postCount,
    canChange,
    timeRemaining,
    joinDate,
    t,
    handlePickImage,
    handleEditUsername,
  ]);

  // Not authenticated - show sign in prompt
  if (!isAuthenticated) {
    return (
      <View style={[styles.container, styles.centered, { backgroundColor: colors.background }]}>
        <Ionicons name="person-circle-outline" size={80} color={colors.textSecondary} />
        <Text style={[styles.signInTitle, fontStyle, { color: colors.text }]}>
          {t('auth.signInRequired')}
        </Text>
        <Text style={[styles.signInSubtitle, fontStyle, { color: colors.textSecondary }]}>
          {t('auth.signInDescription')}
        </Text>
        <Pressable onPress={handleSignInPress}>
          <PixelContainer backgroundColor="#85c363" cornerSize={4} style={styles.signInButton}>
            <Text style={[styles.signInButtonText, fontStyle]}>{t('auth.signIn')}</Text>
          </PixelContainer>
        </Pressable>
      </View>
    );
  }

  // No blocking loading state - show UI immediately with userProfile from AuthContext
  // Posts will show loading indicator inline if needed

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.contentWrapper}>
        <FlatList
          data={posts}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          ListHeaderComponent={renderHeader}
          ListEmptyComponent={renderEmpty}
          ListFooterComponent={renderFooter}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.5}
          contentContainerStyle={posts.length === 0 && styles.emptyList}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={refetch}
              colors={[colors.text]}
              tintColor={colors.text}
            />
          }
        />
      </View>

      {/* Username Setup Modal */}
      {user && (
        <UsernameSetupModal
          userId={user.uid}
          currentUsername={userProfile?.username}
          onUsernameSet={handleUsernameSet}
          isFirstTime={needsUsername}
        />
      )}

      {/* Cooldown Info Modal */}
      <Modal
        visible={showCooldownModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCooldownModal(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setShowCooldownModal(false)}>
          <Pressable onPress={(e) => e.stopPropagation()}>
            <PixelContainer
              backgroundColor={colors.background}
              cornerSize={6}
              style={styles.cooldownDialog}
            >
              <View style={styles.cooldownContent}>
                <Text style={[styles.cooldownTitle, fontStyle, { color: colors.text }]}>
                  {t('username.cooldownTitle')}
                </Text>
                <Text style={[styles.cooldownMessage, fontStyle, { color: colors.textSecondary }]}>
                  {t('username.cooldownMessage', { days: formatTimeRemaining(timeRemaining) })}
                </Text>
                <Pressable onPress={() => setShowCooldownModal(false)} style={styles.cooldownButtonWrapper}>
                  <PixelContainer
                    backgroundColor={colors.primary}
                    cornerSize={4}
                    style={styles.cooldownButton}
                  >
                    <Text style={[styles.cooldownButtonText, fontStyle, { color: '#FFFFFF' }]}>
                      {t('common.done')}
                    </Text>
                  </PixelContainer>
                </Pressable>
              </View>
            </PixelContainer>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      maxWidth: 680,
      width: '100%',
      alignSelf: 'center',
    }),
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  header: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  avatarContainer: {
    marginBottom: 16,
  },
  avatarWrapper: {
    position: 'relative',
  },
  avatar: {
    width: 88,
    height: 88,
    borderRadius: 44,
  },
  cameraOverlay: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  usernameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  username: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  editIconButton: {
    padding: 4,
  },
  editIconDisabled: {
    opacity: 0.5,
  },
  cooldownText: {
    fontSize: 12,
    marginBottom: 12,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 16,
  },
  statItem: {
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 2,
  },
  statDivider: {
    width: 1,
    height: 32,
  },
  memberSince: {
    fontSize: 12,
    marginBottom: 8,
  },
  sectionHeader: {
    width: '100%',
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  footer: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    textAlign: 'center',
  },
  emptyList: {
    flexGrow: 1,
  },
  signInTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  signInSubtitle: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
  },
  signInButton: {
    paddingVertical: 12,
    paddingHorizontal: 32,
  },
  signInButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  cooldownDialog: {
    width: '100%',
    maxWidth: 340,
    padding: 24,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    }),
  },
  cooldownContent: {
    alignItems: 'center',
  },
  cooldownTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  cooldownMessage: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 24,
  },
  cooldownButtonWrapper: {
    width: '100%',
  },
  cooldownButton: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  cooldownButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
